import { useEffect, useRef, useState, type RefObject } from 'react';

// Page bottom padding plus the filter-bar-to-grid gap.
const BOTTOM_GAP = 40;
const ASSUMED_TOP = 128;

const SHORT_VIEWPORT = 500;
const SHORT_GRID_HEIGHT = 320;

// md breakpoint; the mobile layout's fixed header (48) + page padding (24) + 8.
const MOBILE_MAX_WIDTH = 900;
const MOBILE_CHROME = 48 + 24 + 8;

/**
 * Height (px) for a full-page data grid so it fills the viewport and scrolls
 * internally, ported from ar-venus's useAvailableWindowHeight. Resize-aware, with
 * a floor. Measured from where the filter bar ends, because what sits above it
 * differs per page — assuming otherwise pushed the pagination footer off screen.
 */
export function useGridHeight(filterRef?: RefObject<HTMLElement | null>, minHeight = 400): number {
  const [windowHeight, setWindowHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 900,
  );
  const [contentTop, setContentTop] = useState(ASSUMED_TOP);

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );
  const measuredWidth = useRef(windowWidth);

  useEffect(() => {
    const measure = () => {
      const width = window.innerWidth;
      // Phones re-measure on width only: the address bar fires resize mid-scroll.
      if (width >= MOBILE_MAX_WIDTH || width !== measuredWidth.current) setWindowHeight(window.innerHeight);
      measuredWidth.current = width;
      setWindowWidth(width);
      if (filterRef?.current) setContentTop(filterRef.current.getBoundingClientRect().bottom + window.scrollY);
    };
    measure();
    window.addEventListener('resize', measure);

    let ro: ResizeObserver | undefined;
    if (filterRef?.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(filterRef.current);
    }
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [filterRef]);

  if (windowWidth < MOBILE_MAX_WIDTH) return Math.max(windowHeight - MOBILE_CHROME, SHORT_GRID_HEIGHT);

  const available = windowHeight - contentTop - BOTTOM_GAP;

  // A shorter floor: 400px under a header and filters over-scrolls a landscape phone.
  if (windowHeight <= SHORT_VIEWPORT) return Math.max(available, Math.min(minHeight, SHORT_GRID_HEIGHT));

  return Math.max(available, minHeight);
}
